import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "lucide-react";
import { groupDestinations } from "../groupDestinations";
import type { DestinationEntry } from "../groupDestinations";
import { DestinationRow } from "../DestinationRow";
import { HeightProfile } from "../HeightProfile";
import type { DestinationListProps } from "./types";

/**
 * Below this a list is still scannable and every row is one click from its own
 * controls; at 62 it is a wall that fills the panel and scrolls on and on.
 */
const COLLAPSE_FROM = 4;

export const DestinationList = ({
  entries,
  currentCircuitId,
  options,
  edit,
  onHighlight,
  onHoverCircuit,
  heightProfiles,
  boardName = "",
  onShowHeightMarks,
}: DestinationListProps) => {
  const { t } = useTranslation();

  const [openNames, setOpenNames] = useState<string[]>([]);
  const [pinnedId, setPinnedId] = useState<string | null>(null);

  const groups = useMemo(() => groupDestinations({ entries }), [entries]);

  const pinned = useMemo(
    () => entries.find((entry) => entry.destinationId === pinnedId) ?? null,
    [entries, pinnedId],
  );

  const pinnedProfile = useMemo(
    () =>
      heightProfiles?.find((profile) => profile.destinationId === pinnedId) ??
      null,
    [heightProfiles, pinnedId],
  );

  const toggle = (name: string) =>
    setOpenNames((open) =>
      open.includes(name)
        ? open.filter((held) => held !== name)
        : [...open, name],
    );

  // Leaving falls back to whatever he pinned, never to nothing: a pinned device
  // stays marked while he takes the mouse to the drawing.
  const hoverOut = () => onHighlight?.(pinned?.pos ?? null);

  const togglePin = (entry: DestinationEntry) => () => {
    const next = pinnedId === entry.destinationId ? null : entry.destinationId;
    setPinnedId(next ?? null);
    onHighlight?.(next ? (entry.pos ?? null) : null);
  };

  const rowsOf = (
    grouped: DestinationEntry[],
    showName: boolean,
    showLocator: boolean,
  ) =>
    grouped.map((entry) => (
      <Fragment
        key={entry.destinationId ?? `${entry.info.name}-${entry.number}`}
      >
        <DestinationRow
          entry={entry}
          showName={showName}
          showLocator={showLocator}
          currentCircuitId={currentCircuitId}
          options={options}
          edit={edit}
          isPinned={
            entry.destinationId !== undefined &&
            entry.destinationId === pinnedId
          }
          onPin={togglePin(entry)}
          onHoverIn={() => entry.pos && onHighlight?.(entry.pos)}
          onHoverOut={hoverOut}
          onHoverCircuit={onHoverCircuit}
        />
        {pinnedProfile && entry.destinationId === pinnedId && (
          <HeightProfile profile={pinnedProfile} boardName={boardName} />
        )}
      </Fragment>
    ));

  useEffect(() => {
    if (!onShowHeightMarks) return;
    onShowHeightMarks(
      (pinnedProfile?.legs ?? []).flatMap((leg) =>
        leg.kind === "run"
          ? []
          : [{ x: leg.at.x, y: leg.at.y, kind: leg.kind }],
      ),
    );
  }, [pinnedProfile, onShowHeightMarks]);

  useEffect(() => () => onShowHeightMarks?.([]), [onShowHeightMarks]);

  return (
    <>
      {groups.map((group) => {
        if (group.entries.length < COLLAPSE_FROM)
          return (
            <div key={group.name}>
              {rowsOf(group.entries, true, group.hasDistinctLocators)}
            </div>
          );

        const isOpen = openNames.includes(group.name);
        return (
          <div key={group.name} data-testid={`destination-group-${group.name}`}>
            {/* One line for the sixty-two, because one product on one run is
                one thing he is checking. The devices are still each their own
                row, one click away, and that is where their controls live. */}
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={(event) => {
                event.stopPropagation();
                toggle(group.name);
              }}
              className="flex w-full items-center gap-2 rounded border-t border-gray-100 px-1 py-1.5 text-left hover:bg-gray-50"
            >
              {isOpen ? (
                <ChevronDown size={15} className="flex-none text-slate-500" />
              ) : (
                <ChevronRight size={15} className="flex-none text-slate-500" />
              )}
              <span className="flex-none text-sm font-semibold tabular-nums text-slate-700">
                {group.entries.length}
                {" ×"}
              </span>
              <span
                className="min-w-0 flex-1 truncate text-sm text-gray-800"
                title={group.name}
              >
                {group.name || "—"}
              </span>
              <span className="flex-none text-[13px] text-slate-500">
                {t(
                  isOpen
                    ? "smartSurveyor.cablesBeta.destinationGroupCollapse"
                    : "smartSurveyor.cablesBeta.destinationGroupExpand",
                )}
              </span>
            </button>
            {isOpen && (
              <div className="pl-4">
                {rowsOf(group.entries, false, group.hasDistinctLocators)}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};
